import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import {
  listProcurementRequests,
  getProcurementRequestStats,
  type ProcurementRequestStatus,
  type ProcurementRequestSummary,
} from '../../shared/api/procurementRequests.api'
import { Spinner } from '../../shared/ui/Spinner'
import { useCanOpenQueue } from '../procurement/queueAccess'

const STATUS_LABEL: Record<ProcurementRequestStatus, string> = {
  OPEN: 'В работе',
  COMPLETED: 'Завершён',
  ARCHIVED: 'Архив',
}

const STATUS_TONE: Record<ProcurementRequestStatus, string> = {
  OPEN: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  ARCHIVED: 'bg-gray-100 text-gray-500',
}

function formatDateRu(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function progressOf(items: ProcurementRequestSummary['items']): {
  answered: number
  refused: number
  noResponse: number
  pending: number
  total: number
} {
  const counts = { answered: 0, refused: 0, noResponse: 0, pending: 0, total: items.length }
  for (const i of items) {
    if (i.status === 'ANSWERED') counts.answered++
    else if (i.status === 'REFUSED') counts.refused++
    else if (i.status === 'NO_RESPONSE') counts.noResponse++
    else counts.pending++
  }
  return counts
}

export function ProcurementRequestsPage() {
  // У ADMIN очередь закрыта (18.09) — ссылки в неё у него нет (25.09).
  const canQueue = useCanOpenQueue()
  const [statusFilter, setStatusFilter] = useState<ProcurementRequestStatus | ''>('')
  const [search, setSearch] = useState('')

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['procurement-requests', { status: statusFilter, search }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listProcurementRequests({
        status: statusFilter || undefined,
        search: search.trim() || undefined,
        limit: 100,
        cursor: pageParam,
      }),
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
  })
  const requests = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  // Счётчики табов считаются отдельным запросом без статус-фильтра —
  // иначе на вкладке «Завершено» в «В работе» показывался 0.
  const { data: totals = { all: 0, open: 0, completed: 0, archived: 0 } } = useQuery({
    queryKey: ['procurement-requests-stats', { search }],
    queryFn: () => getProcurementRequestStats({ search: search.trim() || undefined }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Запросы поставщикам</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Все BOM-файлы, которые вы отправили поставщикам через WeChat. Загрузка ответа — на
            странице очереди закупки.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setStatusFilter('')}
            className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
              statusFilter === ''
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Все ({totals.all})
          </button>
          <button
            onClick={() => setStatusFilter('OPEN')}
            className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
              statusFilter === 'OPEN'
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            В работе ({totals.open})
          </button>
          <button
            onClick={() => setStatusFilter('COMPLETED')}
            className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
              statusFilter === 'COMPLETED'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Завершено ({totals.completed})
          </button>
          <button
            onClick={() => setStatusFilter('ARCHIVED')}
            className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
              statusFilter === 'ARCHIVED'
                ? 'border-gray-400 bg-gray-100 text-gray-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Архив ({totals.archived})
          </button>
          <div className="flex-1" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по поставщику, коду или партномеру…"
            className="w-64 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          {canQueue ? (
            <p className="text-sm text-gray-500">
              Запросов пока нет. Зайди в{' '}
              <Link to="/procurement/queue" className="text-brand-600 hover:underline">
                очередь закупки
              </Link>
              , отметь позиции и нажми «Скачать запрос».
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              Запросов пока нет. Их формируют закупщики в очереди квот.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Код файла</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Поставщик</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-24">Дата</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-48">
                  Прогресс
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-28">
                  Статус
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Закупщик</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const p = progressOf(r.items)
                const closed = p.answered + p.refused + p.noResponse
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50/60">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link
                        to={`/procurement-requests/${r.id}`}
                        className="text-brand-600 hover:underline"
                      >
                        {r.fileCode}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800">{r.supplierName}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{formatDateRu(r.createdAt)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">
                          {closed}/{p.total}
                        </span>
                        {p.answered > 0 && <span className="text-emerald-600">✓ {p.answered}</span>}
                        {p.refused > 0 && <span className="text-red-500">✗ {p.refused}</span>}
                        {p.noResponse > 0 && (
                          <span className="text-gray-400">— {p.noResponse}</span>
                        )}
                        {p.pending > 0 && <span className="text-blue-500">🕐 {p.pending}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-block px-2 py-0.5 rounded ${STATUS_TONE[r.status]}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {r.procurementUser.name ?? r.procurementUser.email}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {hasNextPage && (
            <div className="border-t border-gray-100 p-3 text-center">
              <button
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-4 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
